import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
    Heading,
    Flex,
    Box,
    useDisclosure,
    Button,
    Divider,
    Spacer,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    SimpleGrid,
    IconButton,
    Text,
    Checkbox,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useOutletContext, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';

import {
    GRID_KEYS,
    getBasisById,
    editBasis,
    getBasisDataFilters,
    mapCommoditiesByTypeAndDescription,
    validateBasisAndLinkedObjectsForDeletion,
    deleteBasisAndLinkedObjects,
} from '../../services/basis';
import { checkItemInputEvent, parseDataFilters, refreshMultisort, exportItemData } from '../../services/items';
import { getIssuesMap } from '../../services/grid';

import { onHasUnsavedChanges } from '../../store/helpers/helpersSlice';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';
import useExceptionToast from '../../hooks/useExceptionToast';

import { SortOrderContext } from '../grid/utils/SortOrderContext';

import ExpandableItemWrapper from '../layout/ExpandableItemWrapper';
import ShowHideSidebarButton from '../layout/ShowHideSidebarButton';
import AutomaticFormikDirtyCheck from '../forms/AutomaticFormikDirtyCheck';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';

import PeakPeriodTab from './PeakPeriodTab';
import SpotPriceTab from './SpotPriceTab';
import ForwardPriceTab from './ForwardPriceTab';
import ModelDefinitionsTab from './ModelDefinitionsTab';
import ScalarTab from './ScalarTab';
import DartSpreadTab from './DartSpreadTab';

import MainModal from '../modal/MainModal';

import Error from '../utils/Error';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import Tooltip from '../utils/Tooltip';
import TabListWrapper from '../utils/TabListWrapper';
import ActionBar from '../utils/ActionBar';
import SecondaryButton from '../utils/SecondaryButton';
import CustomItemIcon from '../utils/CustomItemIcon';
import PromptWrapper from '../utils/PromptWrapper';

import UploadButton from '../itemData/UploadButton';
import DownloadModal from '../utils/DownloadModal';

import { ReactComponent as TrendIcon } from '../../icons/trend.svg';
import { ReactComponent as DeleteLoadIcon } from '../../icons/delete.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as RefreshIcon } from '../../icons/refresh.svg';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { ReactComponent as DownloadIcon } from '../../icons/download.svg';

import GeneralFormValidationsAlert from '../forms/GeneralFormValidationsAlert';

import { mapGeneralValidationMessages } from '../autoform/utils/autoformUtils';
import HelpButtonComponent from 'components/utils/HelpButtonComponent';
import { helpAnchors } from 'constants/help';
import { validateDescription, getInitialFormValues } from './formHelpers';
import ErrorAccordion from 'components/utils/ErrorAccordion';

const Basis = ({
    onBasisEdit: routeOnEdit,
    onBasisDelete: routeOnDelete,
    options: routeOptions,
    basisList: routeBasisList,
}) => {
    const { basisId } = useParams();
    const historyPush = useHistoryPush();
    const { toast } = useCommonToast();
    const intl = useIntl();
    const dispatch = useDispatch();
    const { setSortOrder } = useContext(SortOrderContext);

    const outlet = useOutletContext();
    const { exceptionToast } = useExceptionToast();

    const options = routeOptions ? routeOptions : outlet?.options;
    const basisList = routeBasisList ? routeBasisList : outlet?.basisList;
    const onBasisEdit = routeOnEdit ? routeOnEdit : outlet?.onBasisEdit;
    const onBasisDelete = routeOnDelete ? routeOnDelete : outlet?.onBasisDelete;

    const hasUnsavedChanges = useSelector((state) => state.helpers.hasUnsavedChanges);

    const downloadModal = useDisclosure();
    const deleteBasisWithLinkedObjectsModal = useDisclosure();
    const errorsDisclosure = useDisclosure({ defaultIsOpen: true });
    const warningsDisclosure = useDisclosure({ defaultIsOpen: true });

    const itemId = Number(basisId);
    const item = 'basis';

    const [selectedBasis, setSelectedBasis] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [inEditDescription, setInEditDescription] = useState(false);
    const [key, setKey] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState([]); // unique keys representing which resource is in loading state
    const [selectedTab, setSelectedTab] = useState(0);
    const [filters, setFilters] = useState(() => getBasisDataFilters(item));
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [fieldErrors, setFieldErrors] = useState([]);
    const [fieldWarnings, setFieldWarnings] = useState([]);
    const [generalErrors, setGeneralErrors] = useState([]);
    const [generalWarnings, setGeneralWarnings] = useState([]);
    const [globalErrors, setGlobalErrors] = useState([]);
    const [globalWarnings, setGlobalWarnings] = useState([]);
    const [shouldDeleteLinkedCommodity, setShouldDeleteLinkedCommodity] = useState(true);
    const [shouldDeleteLinkedSpotPrice, setShouldDeleteLinkedSpotPrice] = useState(true);
    const [shouldDeleteLinkedInstrument, setShouldDeleteLinkedInstrument] = useState(true);
    const [deleteValidationResult, setDeleteValidationResult] = useState([]);
    const [isDeleteBasisValidating, setIsDeleteBasisValidating] = useState(false);
    const [isDeletingBasis, setIsDeletingBasis] = useState(false);

    const showAdditionalGridButtons = ![0, 1, 2, 3].includes(selectedTab);

    const allErrors = useMemo(
        () => [...globalErrors, ...generalErrors, ...fieldErrors],
        [fieldErrors, generalErrors, globalErrors]
    );
    const allWarnings = useMemo(
        () => [...globalWarnings, ...generalWarnings, ...fieldWarnings],
        [fieldWarnings, generalWarnings, globalWarnings]
    );

    const refreshTabs = useCallback(() => {
        setKey((prev) => prev + 1);
        setSortOrder((prev) => ({ ...prev, ...refreshMultisort([GRID_KEYS.basisScalar, GRID_KEYS.basisDartSpread]) }));
    }, [setSortOrder]);

    const onLoadingStart = useCallback((key) => {
        setLoadingQueue((prev) => prev.concat(key));
    }, []);

    const onLoadingEnd = useCallback((key) => {
        setLoadingQueue((prev) => prev.filter((p) => p !== key));
    }, []);

    const formLabels = {
        commodity: intl.formatMessage({ id: 'basis_commodity' }),
        priceFormula: intl.formatMessage({ id: 'basis_price_formula' }),
    };

    useEffect(() => {
        setIsLoading(true);
        setLoadingError(false);
        getBasisById(itemId)
            .then((data) => {
                setSelectedBasis(data);
                setLoadingError(false);
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [itemId]);

    useEffect(() => {
        dispatch(onHasUnsavedChanges(isFormDirty));
    }, [dispatch, isFormDirty]);

    const initialFormValues = useMemo(() => {
        return getInitialFormValues(selectedBasis);
    }, [selectedBasis]);

    const normalizeIssues = (data, { setStatus, setTouched }) => {
        // in basis we always receive all issues for all supported fields and grid rows
        // smart mutation of issues is not necessary here

        // make sure to re-open the disclosures again,
        // because the user might have closed them and is submitting the form again
        errorsDisclosure.onOpen();
        warningsDisclosure.onOpen();

        if (data.issues) {
            // when creating a single record we expect at most only 1 issue
            const issue = data.issues[0];

            // handle specific field errors
            let fieldStatus = {};

            for (const field of issue.fields) {
                const errors = field.validationMessages
                    .filter(({ severity }) => severity === 'Error')
                    .map(({ message }) => message);

                const warnings = field.validationMessages
                    .filter(({ severity }) => severity === 'Warning')
                    .map(({ message }) => message);

                fieldStatus[field.name] = { errors, warnings };
            }

            const {
                errors: fieldErrorsMessages,
                warnings: fieldWarningsMessages,
                generalErrors: generalErrorMessages,
                generalWarnings: generalWarningMessages,
            } = getIssuesMap(data.issues)[0];

            setFieldErrors(fieldErrorsMessages);
            setFieldWarnings(fieldWarningsMessages);
            setGeneralErrors(generalErrorMessages);
            setGeneralWarnings(generalWarningMessages);
            setStatus(fieldStatus);
        }

        if (data.globalIssues) {
            // handle global errors and warnings
            const { warnings, errors } = mapGeneralValidationMessages(data.globalIssues);
            setGlobalWarnings(warnings);
            setGlobalErrors(errors);
        }

        if (!data) {
            errorsDisclosure.onClose();
            warningsDisclosure.onClose();

            // reset formik fields status
            setStatus({});

            // reset all server errors and warnings
            setFieldErrors([]);
            setFieldWarnings([]);
            setGeneralErrors([]);
            setGeneralWarnings([]);
            setGlobalWarnings([]);
            setGlobalErrors([]);
        }
    };

    const onFormSubmit = async (values, formikBag) => {
        await updateBasis(values, formikBag);

        onBasisEdit({
            id: selectedBasis.basisId,
            description: values?.description,
        });
    };

    const updateBasis = async (basis, formikBag) => {
        try {
            const successMessage = intl.formatMessage({ id: 'basis_save_changes_success' });

            const { data = {} } = await editBasis(selectedBasis.basisId, { ...basis, useWarnings: true });

            normalizeIssues(data, formikBag);

            setSelectedBasis(basis);
            setInEditDescription(false);

            toast(successMessage);
            formikBag.resetForm({ values: basis });
        } catch (err) {
            if (err.response?.status !== 500) {
                if (err.response?.data?.issues) {
                    normalizeIssues(err.response.data, formikBag);
                }
            }
        }
    };

    const onEditDescription = (isValid) => {
        if (!isValid) {
            return;
        }
        setInEditDescription((prev) => !prev);
    };

    const onDownloadData = useCallback(
        (exportTemplate, sortOrder = {}) => {
            const { startDateFrom, startDateTo } = parseDataFilters(filters);

            const payload = {
                scalarFilter: {
                    startDateFrom,
                    startDateTo,
                    sorting: sortOrder[GRID_KEYS.basisScalar],
                },
                dartSpreadSorting: sortOrder[GRID_KEYS.basisDartSpread],
                exportTemplate,
            };

            return exportItemData(`basis/${itemId}/export`, payload);
        },
        [itemId, filters]
    );

    useEffect(() => {
        if (deleteBasisWithLinkedObjectsModal.isOpen) {
            setDeleteValidationResult([]);

            setShouldDeleteLinkedCommodity(true);
            setShouldDeleteLinkedSpotPrice(true);
            setShouldDeleteLinkedInstrument(true);
        }
    }, [deleteBasisWithLinkedObjectsModal.isOpen]);

    const onLinkedCommodityCheckChange = (e) => {
        const { checked } = e.target;
        setShouldDeleteLinkedCommodity(checked);
    };
    const onLinkedSpotPriceCheckChange = (e) => {
        const { checked } = e.target;
        setShouldDeleteLinkedSpotPrice(checked);
    };
    const onLinkedInstrumentCheckChange = (e) => {
        const { checked } = e.target;
        setShouldDeleteLinkedInstrument(checked);
    };

    const addBasisMessagesForValidation = (basisValidationResult) => {
        //Prepare validation messages for RT Basis
        const deleteCommodityValidationResult = basisValidationResult?.deleteCommodityValidationResponses;
        let commodityMessages = [];
        let RTBasisValidationMessages = [];
        let allMessages = [];

        deleteCommodityValidationResult?.forEach((commodityResponse) => {
            commodityMessages.push(...commodityResponse.messages);
        });

        const RTBasisValidationResponses = basisValidationResult?.rtBasisValidationResponses;
        RTBasisValidationResponses?.forEach((RTBasisResponse) => {
            RTBasisValidationMessages.push(...RTBasisResponse.messages);
        });

        allMessages = [
            'Basis Name : ' + basisValidationResult?.basisDescription,
            'BasisId : ' + basisValidationResult?.basisId,
            ...basisValidationResult?.messages,
            ...commodityMessages,
            ...RTBasisValidationMessages,
        ];

        return allMessages;
    };

    const getValidationForBasisAndLinkedObjects = async (e) => {
        //Return validation messages for the selected Basis and linked object(s)
        setIsDeleteBasisValidating(true);
        deleteBasisWithLinkedObjectsModal.onOpen();

        try {
            const validationResult = await validateBasisAndLinkedObjectsForDeletion(
                [selectedBasis.basisId],
                shouldDeleteLinkedCommodity,
                shouldDeleteLinkedSpotPrice,
                shouldDeleteLinkedInstrument
            );
            const basisValidationResult = validationResult.filter(
                (result) => result.basisId === selectedBasis.basisId
            )[0];

            const commodityValidationResult = basisValidationResult?.deleteCommodityValidationResponses;
            let RTBasisValidationMessages = [];
            let commodityMessages = [];
            let allMessages = [];

            commodityValidationResult?.forEach((commodityResponse) => {
                commodityMessages.push(...commodityResponse.messages);
            });

            const rtBasisValidationResponses = basisValidationResult?.rtBasisValidationResponses;
            rtBasisValidationResponses?.forEach((RTBasisResponse) => {
                const currentRTBasisMessages = addBasisMessagesForValidation(RTBasisResponse);
                RTBasisValidationMessages.push(...currentRTBasisMessages);
            });

            allMessages = [
                'Basis Name : ' + basisValidationResult?.basisDescription,
                'BasisId : ' + basisValidationResult?.basisId,
                ...basisValidationResult?.messages,
                ...commodityMessages,
                ...RTBasisValidationMessages,
            ];
            setDeleteValidationResult(allMessages);
        } finally {
            setIsDeleteBasisValidating(false);
        }
    };

    const addBasisMessagesForDelete = (deleteBasisResult) => {
        //Return the messages for the RT Basis that was deleted
        const deleteCommodityResult = deleteBasisResult?.deleteCommodityResponses;
        let commodityMessages = [];
        let deleteRTBasisMessages = [];
        let allMessages = [];

        deleteCommodityResult?.forEach((commodityResponse) => {
            commodityMessages.push(...commodityResponse.messages);
        });

        const deleteRTBasisResponses = deleteBasisResult?.deleteRTBasisResponses;
        deleteRTBasisResponses?.forEach((RTBasisResponse) => {
            deleteRTBasisMessages.push(...RTBasisResponse.messages);
        });

        allMessages = [
            'Basis Name : ' + deleteBasisResult?.basisDescription,
            'BasisId : ' + deleteBasisResult?.basisId,
            ...deleteBasisResult?.messages,
            ...commodityMessages,
            ...deleteRTBasisMessages,
        ];

        return allMessages;
    };
    const onDeleteBasis = async (targetBasis) => {
        //Delete the selected basis and linked object(s)
        dispatch(onHasUnsavedChanges(false));
        setIsDeletingBasis(true);

        try {
            const deleteBasisResult = await deleteBasisAndLinkedObjects(
                [targetBasis.basisId],
                shouldDeleteLinkedCommodity,
                shouldDeleteLinkedSpotPrice,
                shouldDeleteLinkedInstrument
            );

            const currentDeleteBasisResult = deleteBasisResult?.[0];
            const deleteCommodityResult = currentDeleteBasisResult?.deleteCommodityResponses;

            let commodityMessages = [];
            let deleteRTBasisMessages = [];
            let allMessages = [];
            let deleteBases = [targetBasis.basisId];

            deleteCommodityResult?.forEach((commodityResponse) => {
                commodityMessages.push(...commodityResponse.messages);
            });

            const deleteRTBasisResponses = currentDeleteBasisResult?.deleteRTBasisResponses;
            deleteRTBasisResponses?.forEach((RTBasisResponse) => {
                deleteBases.push(RTBasisResponse.basisId);
                const currentRTBasisMessages = addBasisMessagesForDelete(RTBasisResponse);
                deleteRTBasisMessages.push(...currentRTBasisMessages);
            });

            allMessages = [
                'Basis Name : ' + currentDeleteBasisResult?.basisDescription,
                'BasisId : ' + currentDeleteBasisResult?.basisId,
                ...currentDeleteBasisResult?.messages,
                ...commodityMessages,
                ...deleteRTBasisMessages,
            ];

            //Removed the deleted bases from the bases list
            onBasisDelete(deleteBases);
            historyPush('/basis');

            toast({
                message: intl.formatMessage({ id: 'basis_delete_basis_message' }, { basis: targetBasis.description }),
                slot: (
                    <ErrorAccordion
                        status={'success'}
                        details={
                            <ul>
                                {allMessages.map((str, index) => (
                                    <li key={index}>{str}</li>
                                ))}
                            </ul>
                        }
                    />
                ),
                duration: 30000,
            });
        } catch (err) {
            console.error(`Error deleting basis: ${err}`);
            const errorMessage = err.response?.data?.error || intl.formatMessage({ id: 'common_generic_delete_error' });

            const toastErrorDetails = err.response?.data?.details;
            exceptionToast(errorMessage, toastErrorDetails);
        } finally {
            setIsDeletingBasis(false);
            deleteBasisWithLinkedObjectsModal.onClose();
        }
    };

    return (
        <ExpandableItemWrapper>
            {isLoading || Object.keys(options).length === 0 ? (
                <DefinitionsSkeleton />
            ) : loadingError ? (
                <Box m={6}>
                    <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="basis" />
                </Box>
            ) : (
                <>
                    <Formik initialValues={initialFormValues} onSubmit={onFormSubmit}>
                        {({ isSubmitting, isValid, values }) => (
                            <Form>
                                <AutomaticFormikDirtyCheck
                                    isFormDirty={isFormDirty}
                                    onFormDirtyChange={setIsFormDirty}
                                />
                                <Box position="sticky" top={0} zIndex={50} bg="background-primary">
                                    <Flex pt={3} pl={6} align="center">
                                        <CustomItemIcon isMaster={selectedBasis.securityId} icon={<TrendIcon />} />
                                        {!inEditDescription ? (
                                            <Heading px={2} as="h2" variant="h2" whiteSpace="pre">
                                                {values.description}
                                            </Heading>
                                        ) : (
                                            <InputFormikField
                                                px={3}
                                                flex="1"
                                                maxW="500px"
                                                validate={(value) => validateDescription(value, intl)}
                                                name="description"
                                                type="text"
                                                onKeyDown={checkItemInputEvent}
                                                isFastField
                                            />
                                        )}

                                        <Box display="inline-block" mr={4}>
                                            <Tooltip
                                                label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}
                                            >
                                                <IconButton
                                                    aria-label={intl.formatMessage({
                                                        id: 'common_edit_btn_tooltip_and_label',
                                                    })}
                                                    variant="circular-icon"
                                                    leftIcon={<EditIcon />}
                                                    onClick={() => onEditDescription(isValid)}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Flex>

                                    <ActionBar>
                                        <ShowHideSidebarButton />

                                        <Spacer display={{ base: 'none', xl: 'block' }} />
                                        <Tooltip label={intl.formatMessage({ id: 'basis_delete_btn_tooltip' })}>
                                            <Button
                                                leftIcon={<DeleteLoadIcon />}
                                                type="button"
                                                variant="special"
                                                onClick={deleteBasisWithLinkedObjectsModal.onOpen}
                                                size="sm"
                                            >
                                                <FormattedMessage id="basis_delete_btn" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip label={intl.formatMessage({ id: 'common_save_changes_button' })}>
                                            <Button
                                                leftIcon={<SaveIcon />}
                                                isLoading={isSubmitting}
                                                type="submit"
                                                size="sm"
                                                variant="primary-success"
                                            >
                                                <FormattedMessage id="common_save_changes_button" />
                                            </Button>
                                        </Tooltip>
                                    </ActionBar>
                                    <Divider />
                                </Box>

                                <SimpleGrid py={5} px={6} columns={{ xl: 3 }} spacingX={6}>
                                    <ComboboxFormikField
                                        id="commodityId"
                                        name="commodityId"
                                        options={mapCommoditiesByTypeAndDescription(options.commodityTypes)}
                                        valueKey="id"
                                        labelKey="description"
                                        label={formLabels.commodity}
                                        placeholderValue=""
                                        placeholderLabel={intl.formatMessage({ id: 'common_forms_select_option' })}
                                        isRequired
                                    />
                                    <ComboboxFormikField
                                        id="priceFormulaId"
                                        name="priceFormulaId"
                                        options={options.priceFormula}
                                        valueKey="id"
                                        labelKey="description"
                                        label={formLabels.priceFormula}
                                        showPlaceholder={true}
                                        placeholderValue={null}
                                    />
                                </SimpleGrid>

                                {allErrors.length > 0 && (
                                    <GeneralFormValidationsAlert
                                        mx={6}
                                        w="unset"
                                        isOpen={errorsDisclosure.isOpen}
                                        onClose={errorsDisclosure.onClose}
                                        messages={allErrors}
                                    />
                                )}

                                {allWarnings.length > 0 && (
                                    <GeneralFormValidationsAlert
                                        mx={6}
                                        w="unset"
                                        status="warning"
                                        title={<FormattedMessage id="common_warnings" />}
                                        isOpen={warningsDisclosure.isOpen}
                                        onClose={warningsDisclosure.onClose}
                                        messages={allWarnings}
                                    />
                                )}

                                <Divider />

                                <Tabs
                                    px={6}
                                    pb={20}
                                    isLazy
                                    isManual
                                    lazyBehavior="keepMounted"
                                    key={key}
                                    index={selectedTab}
                                    onChange={setSelectedTab}
                                >
                                    <CustomTabList
                                        w="100%"
                                        flexWrap={{ base: 'wrap', xl: 'nowrap' }}
                                        alignItems="center"
                                    >
                                        <TabListWrapper currentTab={selectedTab} tabCount={7} minW={0}>
                                            <CustomTab as="div">
                                                <FormattedMessage id="basis_tab_peak_periods" />
                                                <HelpButtonComponent helpAnchor={helpAnchors.BASIS_PEAK_PERIOD} />
                                            </CustomTab>

                                            <CustomTab as="div">
                                                <FormattedMessage id="basis_tab_model_definition" />
                                                <HelpButtonComponent helpAnchor={helpAnchors.BASIS_MODEL_DEFINITION} />
                                            </CustomTab>

                                            <CustomTab as="div">
                                                <FormattedMessage id="basis_tab_forward_price" />
                                                <HelpButtonComponent helpAnchor={helpAnchors.BASIS_FORWARD_PRICE} />
                                            </CustomTab>

                                            <CustomTab as="div">
                                                <FormattedMessage id="basis_tab_spot_price" />
                                                <HelpButtonComponent helpAnchor={helpAnchors.BASIS_SPOT_PRICE} />
                                            </CustomTab>

                                            <CustomTab as="div">
                                                <FormattedMessage id="basis_tab_scaler" />
                                                <HelpButtonComponent helpAnchor={helpAnchors.BASIS_SCALER} />
                                            </CustomTab>

                                            {/* <CustomTab isDisabled={true}>
                                                <Tooltip
                                                    label={intl.formatMessage({
                                                        id: 'common_feature_in_development',
                                                    })}
                                                >
                                                    <FormattedMessage id="basis_tab_sub-hourly_details" />
                                                </Tooltip>
                                            </CustomTab> */}

                                            <CustomTab as="div">
                                                <FormattedMessage id="basis_tab_dart_spread" />
                                            </CustomTab>
                                        </TabListWrapper>

                                        <Flex ml="auto" order={{ base: -1, xl: 0 }} w={{ base: '100%', xl: 'auto' }}>
                                            {showAdditionalGridButtons && (
                                                <>
                                                    <SecondaryButton
                                                        leftIcon={<DownloadIcon />}
                                                        w="auto !important"
                                                        mx={2}
                                                        size="sm"
                                                        type="button"
                                                        variant="secondary"
                                                        onClick={downloadModal.onOpen}
                                                    >
                                                        <FormattedMessage id="common_download_btn_tooltip_and_label" />
                                                    </SecondaryButton>

                                                    <UploadButton
                                                        type="basis"
                                                        itemId={itemId}
                                                        hasLabel={true}
                                                        mr={4}
                                                        w="auto !important"
                                                        onAfterUploadChange={refreshTabs}
                                                    />
                                                </>
                                            )}

                                            <SecondaryButton
                                                onClick={refreshTabs}
                                                size="sm"
                                                variant="secondary"
                                                leftIcon={<RefreshIcon />}
                                                isLoading={loadingQueue.length > 0}
                                                loadingText={intl.formatMessage({ id: 'common_loading' })}
                                                textTransform="capitalize"
                                            >
                                                <FormattedMessage id="common_refresh" />
                                            </SecondaryButton>
                                        </Flex>
                                    </CustomTabList>

                                    <TabPanels mt={2}>
                                        <TabPanel>
                                            <PeakPeriodTab options={options} />
                                        </TabPanel>

                                        <TabPanel>
                                            <ModelDefinitionsTab
                                                options={options}
                                                basisModelTypeId={values?.basisModelTypeId}
                                            />
                                        </TabPanel>

                                        <TabPanel>
                                            <ForwardPriceTab options={options} />
                                        </TabPanel>

                                        <TabPanel>
                                            <SpotPriceTab options={options} />
                                        </TabPanel>

                                        <TabPanel>
                                            <ScalarTab
                                                tab="1"
                                                name={GRID_KEYS.basisScalar}
                                                options={options}
                                                basis={selectedBasis}
                                                filters={filters}
                                                onFiltersChange={setFilters}
                                                item={item}
                                                onLoadingStart={onLoadingStart}
                                                onLoadingEnd={onLoadingEnd}
                                            />
                                        </TabPanel>

                                        <TabPanel>
                                            <DartSpreadTab
                                                tab="2"
                                                name={GRID_KEYS.basisDartSpread}
                                                options={options}
                                                basis={selectedBasis}
                                                basisList={basisList}
                                                onLoadingStart={onLoadingStart}
                                                onLoadingEnd={onLoadingEnd}
                                            />
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </Form>
                        )}
                    </Formik>

                    {deleteBasisWithLinkedObjectsModal.isOpen && (
                        <MainModal
                            isOpen
                            onClose={deleteBasisWithLinkedObjectsModal.onClose}
                            header={
                                <FormattedMessage
                                    id="common_delete_modal_heading"
                                    values={{ item: selectedBasis.description }}
                                />
                            }
                            content={
                                <>
                                    <Text variant="regular" mb={2}>
                                        This basis maybe linked to Commodity, Spot Price(s) and Empty Instrument(s).
                                        Please select from the options below to choose the linked objects you want to
                                        delete along with the basis. You can also choose to validate the linked objects
                                        before deleting.
                                    </Text>
                                    <Box display="flex" flexDirection="column" gap="10px">
                                        <Checkbox
                                            onChange={(e) => {
                                                onLinkedCommodityCheckChange(e);
                                            }}
                                            isDisabled={isDeleteBasisValidating || isDeletingBasis}
                                            defaultChecked={true}
                                        >
                                            Delete Linked Commodities
                                        </Checkbox>
                                        <Checkbox
                                            onChange={(e) => {
                                                onLinkedSpotPriceCheckChange(e);
                                            }}
                                            isDisabled={isDeleteBasisValidating || isDeletingBasis}
                                            defaultChecked={true}
                                        >
                                            Delete Linked Spot Price(s)
                                        </Checkbox>
                                        <Checkbox
                                            onChange={(e) => {
                                                onLinkedInstrumentCheckChange(e);
                                            }}
                                            isDisabled={isDeleteBasisValidating || isDeletingBasis}
                                            defaultChecked={true}
                                        >
                                            Delete Linked Empty Instrument Item(s)
                                        </Checkbox>
                                    </Box>
                                </>
                            }
                            footerLeftSlot={
                                <Button
                                    onClick={(e) => {
                                        getValidationForBasisAndLinkedObjects(e);
                                    }}
                                    isLoading={isDeleteBasisValidating || isDeletingBasis}
                                    disabled={isDeleteBasisValidating || isDeletingBasis}
                                >
                                    Display Linked Objects for this basis
                                </Button>
                            }
                            secondaryButton={
                                <Button
                                    variant="secondary"
                                    onClick={deleteBasisWithLinkedObjectsModal.onClose}
                                    isDisabled={isDeleteBasisValidating || isDeletingBasis}
                                >
                                    <Box as="span" textTransform="capitalize">
                                        <FormattedMessage id="common_close" />
                                    </Box>
                                </Button>
                            }
                            footerRightSlot={
                                <Button
                                    ml={3}
                                    type="button"
                                    variant="special"
                                    onClick={() => {
                                        onDeleteBasis(selectedBasis);
                                    }}
                                    disabled={isDeleteBasisValidating || isDeletingBasis}
                                >
                                    <Box as="span" textTransform="capitalize">
                                        <FormattedMessage id="basis_delete_basis" />
                                    </Box>
                                </Button>
                            }
                            accordionSlot={
                                deleteValidationResult.length > 0 && (
                                    <Box my={4} mx={5}>
                                        <ErrorAccordion
                                            status="success"
                                            details={
                                                <ul>
                                                    {deleteValidationResult.map((str, index) => (
                                                        <li key={index}>{str}</li>
                                                    ))}
                                                </ul>
                                            }
                                            defaultOpen={true}
                                        />
                                    </Box>
                                )
                            }
                        />
                    )}
                </>
            )}

            {downloadModal.isOpen && (
                <DownloadModal
                    isOpen
                    bodyMessage={<FormattedMessage id="basis_download_modal_explanation" />}
                    onClose={downloadModal.onClose}
                    onDownloadData={(sortOrder) => onDownloadData(false, sortOrder)}
                    onDownloadTemplate={() => onDownloadData(true)}
                />
            )}
            <PromptWrapper
                when={hasUnsavedChanges}
                header={<FormattedMessage id="common_discard_changes" />}
                content={<FormattedMessage id="common_confirmation_explanation" />}
            />
        </ExpandableItemWrapper>
    );
};

const CustomTabList = styled(TabList)`
    overflow-x: auto;
    overflow-y: hidden;
    padding-top: 5px;
    border-bottom: 1px solid var(--chakra-colors-gray-200);
`;

const CustomTab = styled(Tab)`
    margin-right: 24px;
    margin-bottom: 0;
    white-space: nowrap;

    :hover {
        cursor: pointer;
    }

    .tooltip_wrapper {
        transform: translate(-50%, -110%) !important;
    }
`;

export default Basis;
